import { Button, Flex, Tooltip } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { NavigationBlocker } from "src/components/NavigationBlocker";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  deleteUnpublishedFormSchema,
  getFormTemplate,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import { useCalculateDiff, useEditFormTemplateContext } from "./context";
import { useCheckPendingQuestionEdits } from "./useCheckPendingQuestionEdits";
type FormButtonsProps = {};
export const FormButtons: React.FC<FormButtonsProps> = () => {
  const {
    state,
    formTemplateId,
    dispatch,
    isPublishedMode,
    hasUnsavedChanges: hasChanges,
    hasUnpublishedChanges,
    hasDirtyForm,
    saveFormSchema,
  } = useEditFormTemplateContext();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [isClearingUnpublishedChanges, setIsClearingUnpublishedChanges] =
    React.useState(false);
  const { checkPendingQuestionEdits } = useCheckPendingQuestionEdits(
    state,
    dispatch
  );

  const { confirm, confirmationDialog } = useConfirmationDialog({
    body: "This will remove all of your unpublished changes and reset any questions changed to match the last published version.",
    header: "Are you sure?",
    confirmButton: {
      label: "Reset",
      colorScheme: "red",
    },
  });

  const toast = useAvelaToast();

  const { unsafeCalculateDiff } = useCalculateDiff();

  const handleSave = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const validations = checkPendingQuestionEdits();
      if (!validations.success) {
        return;
      }

      await saveFormSchema();
      try {
        await unsafeCalculateDiff();
      } catch (error) {
        toast.error({
          title: "Error detecting unpublished changes",
        });
        console.error(error);
      }

      toast({
        title: "Form saved",
        description: "Your changes have been saved.",
        isClosable: true,
        status: "success",
      });
    } catch (error) {
      toast.genericError();
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [unsafeCalculateDiff, checkPendingQuestionEdits, saveFormSchema, toast]);

  const validateFormSchema = FSQ.useValidateFormSchema();

  const handleValidateForm = React.useCallback(async () => {
    setIsValidating(true);
    try {
      const schema = state.get("draft");
      const validations = checkPendingQuestionEdits();
      if (!validations.success) {
        return;
      }

      const response = await validateFormSchema.mutateAsync({
        path: {
          formTemplateId,
        },
        body: {
          schema,
        },
      });

      if (response.data?.data.validationResults) {
        dispatch({
          type: "SetValidations",
          validations: response.data.data.validationResults,
        });
      }
      const errorCount = response.data?.data.validationResults.length ?? 0;
      if (errorCount === 0) {
        toast({
          title: "No errors found",
          isClosable: true,
          status: "success",
        });
      } else {
        toast.error({
          title: `${errorCount} ${maybePluralize(
            errorCount,
            "error",
            "errors"
          )} found`,
          description: "",
          duration: null,
        });
      }
    } catch (error) {
      toast.genericError();
      console.error(error);
    } finally {
      setIsValidating(false);
    }
  }, [
    state,
    checkPendingQuestionEdits,
    validateFormSchema,
    formTemplateId,
    dispatch,
    toast,
  ]);

  const clientQuery = useQueryClient();
  const handleClearUnpublishedFormSchema = React.useCallback(async () => {
    setIsClearingUnpublishedChanges(true);
    try {
      if (await confirm()) {
        const result = await deleteUnpublishedFormSchema({
          path: {
            formTemplateId,
          },
        });

        if (!result.data?.data?.formSchemaId) {
          toast.genericError();
        }

        clientQuery.invalidateQueries({
          queryKey: [FSQ.useGetFormTemplateKey],
        });

        const { data: ftResult } = await getFormTemplate({
          path: { formTemplateId },
          query: {
            version: "Published",
          },
        });

        if (!ftResult) {
          toast.genericError();
        } else {
          dispatch({
            type: "SetFormTemplate",
            formTemplate: ftResult.data,
          });

          try {
            await unsafeCalculateDiff();
          } catch (error) {
            if (!toast.isActive("diffError")) {
              toast.error({
                title: "Error detecting unpublished changes",
              });
            }
            console.error(error);
          }

          toast({
            title: "Unpublished changes cleared",
            isClosable: true,
            status: "success",
          });
        }
      }
    } catch (error: any) {
      if (error.error?.message === "Form template has no unpublished schema") {
        if (hasChanges) {
          dispatch({ type: "ClearUnsavedChanges" });
        } else {
          toast({
            title: "Clear unpublished changes",
            description: "No unpublished changes to clear",
            isClosable: true,
            status: "warning",
          });
        }

        return;
      }

      toast.genericError();
      console.error(error);
    } finally {
      setIsClearingUnpublishedChanges(false);
    }
  }, [
    confirm,
    formTemplateId,
    clientQuery,
    toast,
    dispatch,
    unsafeCalculateDiff,
    hasChanges,
  ]);

  // Add keyboard shortcut for saving (Cmd+S / Ctrl+S)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Cmd+S (Mac) or Ctrl+S (Windows/Linux) is pressed
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault(); // Prevent browser's default save dialog

        // Only trigger save if there are changes and not currently submitting
        if (hasChanges && !isSaving) {
          handleSave();
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasChanges, isSaving, handleSave]);

  return (
    <Flex flexDirection="row" gap="4">
      {isPublishedMode && (
        <ConditionalTooltip
          label="No unpublished changes"
          showTooltip={!hasUnpublishedChanges}
        >
          <Button
            variant="ghost"
            colorScheme="gray"
            isLoading={isClearingUnpublishedChanges}
            isDisabled={!hasUnpublishedChanges}
            onClick={async () => {
              handleClearUnpublishedFormSchema();
            }}
            gridColumn="3"
          >
            Clear unpublished changes
          </Button>
        </ConditionalTooltip>
      )}
      <Button
        isLoading={isValidating}
        variant="ghost"
        colorScheme="gray"
        onClick={handleValidateForm}
      >
        Check for errors
      </Button>
      <Tooltip label="Save (Ctrl+S/⌘+S)" placement="top" hasArrow>
        <Button
          variant="solid"
          type="submit"
          isDisabled={isSaving}
          isLoading={isSaving}
          onClick={handleSave}
          gridColumn="4"
        >
          Save
        </Button>
      </Tooltip>
      {confirmationDialog}
      <NavigationBlocker hasChanges={hasChanges || hasDirtyForm} />
    </Flex>
  );
};
